import { createContext, useContext, type ReactNode } from 'react';

export const defaultUiText = {
  loadTeamError: 'Could not load team administration.',
  saveError: 'Could not save. Please try again.',
  saveTeamError: 'Could not save the team. Please try again.',
  invalidTime: 'Invalid format. Try: 9:00 am, 13:00, 2pm',

  addTime: 'Add time',
  select: 'Select',
  selectOption: 'Select option',
  back: 'Back',
  teamAdministration: 'Team administration',
  retry: 'Retry',
  loadingTeam: 'Loading team…',
  members: 'Members',
  teamAdmin: 'Team admin',
  remove: 'Remove',
  fromTeam: 'from team',
  inviteOrPreassignAMember: 'Invite or preassign a member',
  memberEmail: 'Member email',
  roles: 'Roles',
  principalInvestigator: 'Principal investigator',
  alsoAssignTeamAdmin: 'Also assign Team admin',
  sendInvitationEmail: 'Send invitation email',
  sendInvitation: 'Send invitation',
  preassignMember: 'Preassign member',
  newUsersChooseTheirPasswordThroughTheEmailLink:
    'New users choose their password through the email link. Existing users keep their account.',
  invitations: 'Invitations',
  registeredMembers: 'Registered members',
  joinedTeam: 'Joined',
  pendingInvitations: 'Pending invitations',
  pendingInvitationsHint:
    'Pending means they have not joined yet. Email delivery is shown separately. Updates every minute.',
  noPendingInvitations: 'No pending invitations.',
  invitationHistory: 'Invitation history',
  invitationClaimed: 'Claimed',
  invitationPending: 'Pending signup',
  invitationCancelled: 'Cancelled',
  invitationEmailSent: 'Email sent',
  invitationEmailSending: 'Sending email',
  invitationEmailFailed: 'Email failed',
  invitationEmailNotSent: 'Email not sent',
  invitationEmailDelivery: 'Email delivery',
  email: '· Email:',
  sendInvitationTo: 'Send invitation to',
  cancelInvitation: 'Cancel invitation',
  principalInvestigators: 'Principal investigators',
  findInCatalog: 'Find in the investigator catalog',
  searchingCatalog: 'Searching the catalog…',
  noCatalogMatches: 'No investigators match this name.',
  catalogSearchFailed: 'Could not search the investigator catalog.',
  catalogIdentity: 'Catalog identity',
  linkCatalogIdentity: 'Link catalog identity',
  unlinkCatalogIdentity: 'Unlink catalog identity',
  changeSelection: 'Change',
  studiesLinkedThrough: 'Studies linked through',
  noCatalogIdentityLinked:
    'No catalog identity is linked yet, so this member brings no studies to the team.',
  tenantAdminLinksIdentity: 'A tenant admin can link their catalog entry in Cortex.',
  catalogPiWithoutAccount: 'Catalog PI without an account',
  piIdentityHint:
    'Choose the catalog entry for this person. Their catalog studies reach the team once the membership is active.',
  studies: 'studies',
  removePi: 'Remove PI',
  additionalEmail: 'Additional email',
  additionalEmailHint:
    'A second address this person is known by, such as their entry in the investigator catalog. It counts for PI identity like their sign-in email.',
  inviteOutsideDomain: "{{domain}} is not one of this tenant's email domains. Invite them anyway.",
  outsideDomainNeedsTenantAdmin:
    "{{domain}} is not one of this tenant's email domains. Ask a tenant admin to invite this person.",
  moveFromOtherTenant:
    'Move this person out of their current tenant and into this team. They lose access to their old tenant and team.',
  saveAdditionalEmail: 'Save additional email',
  removeAdditionalEmail: 'Remove additional email',
  additionalEmails: 'Additional emails',
  additionalEmailsHint:
    'Other addresses this person is known by, such as their entries in CTIS, DRKS or ClinicalTrials.gov. Each counts for PI identity like their sign-in email, so a principal investigator found under any of them brings their studies to the team.',
  oneAdditionalEmailHint:
    'This Rudi keeps one additional address per member. Several addresses arrive with the next Rudi release.',
  saveAdditionalEmails: 'Save emails',
  addAdditionalEmail: 'Add an additional email',
  addEmail: 'Add',
  removeEmail: 'Remove {{email}}',
  invalidAdditionalEmail: '{{email}} is not an email address.',
  additionalEmailIsSignIn: 'This is already their sign-in email.',
  tooManyAdditionalEmails: 'A member can have up to {{count}} additional emails.',
  chooseRoles: 'Choose roles',
  searchRoles: 'Search roles',
  noRolesMatch: 'No roles match.',
  moreRoles: '+{{count}} more',
  rolesSelected: '{{count}} selected',
  inactiveRole: 'Inactive',
  roleCategoryAll: 'All',
  roleCategoryClinical: 'Clinical',
  roleCategoryBusiness: 'Business',
  roleCategoryAdministration: 'Administration',
  roleCategoryCustom: 'Custom',
  roleCategorySelected: 'Selected',
  furtherInstruction: 'Further instruction',
  toggleSidebar: 'Toggle Sidebar',
  close: 'Close',
  editName: 'Edit {{name}}',
  edit: 'Edit',
  editTeam: 'Edit team',
  changeTheTeamSNameAndSlug: "Change the team's name and slug.",
  teamName: 'Team name',
  teamSlug: 'Team slug',
  useLowercaseLettersNumbersAndHyphensForExampleDerma:
    'Use lowercase letters, numbers, and hyphens, for example derma-leipzig.',
  cancel: 'Cancel',
  saving: 'Saving…',
  saveChanges: 'Save changes',
  previousSlide: 'Previous slide',
  nextSlide: 'Next slide',
  breadcrumb: 'breadcrumb',
  more: 'More',
  clearTime: 'Clear time',
  selectTime: 'Select Time',
  switchTo24h: 'Switch to 24h',
  switchToAmPm: 'Switch to AM/PM',
  clear: 'Clear',
  done: 'Done',
  clearIcon: 'Clear icon',
  pagination: 'pagination',
  goToPreviousPage: 'Go to previous page',
  previous: 'Previous',
  goToNextPage: 'Go to next page',
  next: 'Next',
  morePages: 'More pages',
  loading: 'Loading',
};

export type UiTextKey = keyof typeof defaultUiText;
export type UiMessages = Partial<Record<UiTextKey, string>>;
const UiLocaleContext = createContext<string | undefined>(undefined);
export const useUiLocale = () => useContext(UiLocaleContext);

const UiTextContext = createContext<UiMessages>({});

export function UiTextProvider({
  messages,
  locale,
  children,
}: {
  messages: UiMessages;
  locale?: string;
  children: ReactNode;
}) {
  return (
    <UiLocaleContext.Provider value={locale}>
      <UiTextContext.Provider value={messages}>{children}</UiTextContext.Provider>
    </UiLocaleContext.Provider>
  );
}

export function useUiText() {
  const messages = useContext(UiTextContext);
  return (key: UiTextKey, values: Record<string, string | number> = {}) =>
    (messages[key] ?? defaultUiText[key]).replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
      String(values[name] ?? match)
    );
}
